import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Radio,
} from '@you/ui'
import { Icon } from '@/components/Icon'
import { useEffect, useState } from 'react'
import { datasetFilesService } from '@/api/services/datasetFiles.service'
import { DbDataTypeEnum } from '@/api/types/datasetFiles.type'
import { SheetInfo } from '@/api/types/datasetFiles.type'

// 生成1-20的选项
const generateNumberOptions = (count: number) =>
  Array.from({ length: count }, (_, i) => ({
    value: (i + 1).toString(),
    label: `第${i + 1}行`,
  }))

// 表头选项
const ROW_OPTIONS = generateNumberOptions(20)

// 表结构列接口
export interface TableColumn {
  id: string
  isIndex: boolean
  columnName: string
  description: string
  dataType: string
}

interface DatasetSettingTableProps {
  dataTable: string
  headerRow: string
  dataStartRow: string
  columns: TableColumn[]
  onDataTableChange: (value: string) => void
  onHeaderRowChange: (value: string) => void
  onDataStartRowChange: (value: string) => void
  onColumnsChange: (columns: TableColumn[]) => void
  sheetInfo?: SheetInfo // 添加 sheetInfo 属性
}

const DatasetSettingTable = ({
  dataTable,
  headerRow,
  dataStartRow,
  columns,
  onDataTableChange,
  onHeaderRowChange,
  onDataStartRowChange,
  onColumnsChange,
  sheetInfo,
}: DatasetSettingTableProps) => {
  useEffect(() => {
    console.log('dataTable', dataTable)
  }, [dataTable])
  // 数据类型选项状态
  const [dataTypeOptions, setDataTypeOptions] = useState<DbDataTypeEnum[]>([])

  // 获取数据类型选项
  useEffect(() => {
    const fetchDataTypes = async () => {
      try {
        const response = await datasetFilesService.getDbDataTypeEnum()
        setDataTypeOptions(response)
      } catch (error) {
        console.error('获取数据类型失败:', error)
      }
    }
    fetchDataTypes()
  }, [])

  // 处理表头行变化
  const handleHeaderRowChange = (value: string) => {
    onHeaderRowChange(value)
    // 自动设置数据起始行为表头行的下一行
    const nextRow = (parseInt(value) + 1).toString()
    onDataStartRowChange(nextRow)

    // 更新列信息
    if (sheetInfo?.rowDataList) {
      const headerRowIndex = parseInt(value) - 1
      const headerRowData = sheetInfo.rowDataList[headerRowIndex]?.cellData

      if (headerRowData) {
        const newColumns = Object.entries(headerRowData).map(
          ([_, value], index) => ({
            id: index.toString(),
            isIndex: false,
            columnName: String(value),
            description: '',
            dataType: '',
          })
        )
        onColumnsChange(newColumns)
      }
    }
  }

  // 添加新列
  const addColumn = () => {
    const newColumn: TableColumn = {
      id: Date.now().toString(),
      isIndex: false,
      columnName: '',
      description: '',
      dataType: '',
    }
    onColumnsChange([...columns, newColumn])
  }

  // 标题渲染
  const renderTitle = (title: string) => {
    return (
      <h3 className="text-base text-[#1D2129] mb-2">
        {title} <span className="text-red-500">*</span>
      </h3>
    )
  }

  // 删除列
  const removeColumn = (id: string) => {
    if (columns.length > 1) {
      onColumnsChange(columns.filter((col) => col.id !== id))
    }
  }

  // 更新列数据
  const updateColumn = (
    id: string,
    field: keyof TableColumn,
    value: string | boolean
  ) => {
    if (field === 'isIndex' && value === true) {
      // 如果是设置索引，先清除其他列的索引状态
      onColumnsChange(
        columns.map((col) => ({
          ...col,
          isIndex: col.id === id ? true : false,
        }))
      )
    } else {
      onColumnsChange(
        columns.map((col) => (col.id === id ? { ...col, [field]: value } : col))
      )
    }
  }

  return (
    <div className="space-y-6">
      {/* 上部分：三栏必填项 */}
      <div className="grid grid-cols-3 gap-6">
        {/* 数据表 */}
        <div>
          {renderTitle('数据表')}
          <Select
            value={sheetInfo?.sheetName || 'sheet1'}
            onValueChange={onDataTableChange}
            disabled
          >
            <SelectTrigger className="text-sm bg-white">
              <SelectValue>{sheetInfo?.sheetName || 'Sheet1'}</SelectValue>
            </SelectTrigger>
            {sheetInfo?.sheetName && (
              <SelectContent>
                <SelectItem value={sheetInfo.sheetName}>
                  {sheetInfo.sheetName}
                </SelectItem>
              </SelectContent>
            )}
          </Select>
        </div>

        {/* 表头 */}
        <div>
          {renderTitle('表头')}
          <Select
            value={headerRow || '1'}
            onValueChange={handleHeaderRowChange}
          >
            <SelectTrigger className="text-sm bg-white">
              <SelectValue placeholder="请选择表头行" />
            </SelectTrigger>
            <SelectContent>
              {ROW_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* 数据起始行 */}
        <div>
          {renderTitle('数据起始行')}
          <Select
            value={dataStartRow || '2'}
            onValueChange={onDataStartRowChange}
          >
            <SelectTrigger className="text-sm bg-white">
              <SelectValue placeholder="请选择数据起始行" />
            </SelectTrigger>
            <SelectContent>
              {ROW_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* 下部分：表结构 */}
      <div>
        {renderTitle('表结构')}

        {/* 表结构容器 */}
        <div className="border rounded-lg overflow-hidden border-[#E9DFFF] bg-[#FCFCFF]">
          {/* 表头 */}
          <div className="flex items-center font-medium text-base py-3 border-b border-[#E9DFFF] [&>*]:text-[#1D2129] [&>*]:text-left [&>*]:px-2 [&>*]:flex-1">
            <div style={{ flex: 'none', width: '80px', textAlign: 'center' }}>
              索引
            </div>
            <div>列名</div>
            <div>描述</div>
            <div>数据类型</div>
            <div style={{ flex: 'none', width: '80px', textAlign: 'center' }}>
              操作
            </div>
          </div>

          {/* 内容区域 */}
          {columns.map((column) => (
            <div
              key={column.id}
              className="flex items-center h-[68px] [&>*]:px-2 [&>*]:flex-1 [&>*]:text-left"
            >
              {/* 索引 */}
              <div
                style={{
                  flex: 'none',
                  width: '80px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Radio
                  checked={column.isIndex}
                  onChange={(e) =>
                    updateColumn(column.id, 'isIndex', e.target.checked)
                  }
                  size="md"
                />
              </div>

              {/* 列名 */}
              <div>
                <Input
                  value={column.columnName}
                  onChange={(e) =>
                    updateColumn(column.id, 'columnName', e.target.value)
                  }
                  placeholder="请输入列名"
                  className="text-sm"
                />
              </div>

              {/* 描述 */}
              <div>
                <Input
                  value={column.description}
                  onChange={(e) =>
                    updateColumn(column.id, 'description', e.target.value)
                  }
                  placeholder="请输入描述"
                  className="text-sm"
                />
              </div>

              {/* 数据类型 */}
              <div>
                <Select
                  value={column.dataType}
                  onValueChange={(value) =>
                    updateColumn(column.id, 'dataType', value)
                  }
                >
                  <SelectTrigger className="text-sm bg-white">
                    <SelectValue placeholder="请选择数据类型" />
                  </SelectTrigger>
                  <SelectContent>
                    {dataTypeOptions.map((option) => (
                      <SelectItem key={option.key} value={option.key}>
                        {option.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* 操作 */}
              <div
                style={{
                  flex: 'none',
                  width: '80px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {columns.length > 1 && (
                  <button
                    onClick={() => removeColumn(column.id)}
                    className="flex items-center justify-center w-6 h-6 text-gray-500 hover:text-red-500 transition-colors"
                    title="删除列"
                    aria-label="删除列"
                  >
                    <Icon name="delete" size={20} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* 添加列按钮 */}
        <div className="mt-4">
          <button
            onClick={addColumn}
            className="px-4 py-2 text-sm text-[#8752FF] border border-[#8752FF] rounded-lg hover:bg-[#8752FF] hover:text-white transition-colors"
          >
            添加列
          </button>
        </div>
      </div>
    </div>
  )
}

export default DatasetSettingTable
